<script setup lang="ts">
import { ContentComp } from '@/components';
import { IconComp, InputText, ResetPassword, InputCode } from '@/components';
import { UserDao } from '@/dao';
import { ResetPasswordRequest } from '@/objects/generated';
import { ref } from 'vue';

/**
 * We are waiting for the email with the reset code
 */
const waitingForResetCode = ref<boolean>(false);

/**
 * Ref to the Error message in the reset modal
 */
const resetErrormessage = ref<string>('');

const value = defineModel<ResetPasswordRequest>({
    required: true,
});

const emits = defineEmits<{
    (e: 'passwordUpdated', status: boolean): void;
}>();

/**
 * Request a new password
 */
const RequestNewPassword = async () => {
    try {
        await UserDao.RequestPasswordReset(value.value);
        waitingForResetCode.value = true;
    } catch (error: any) {
        resetErrormessage.value = error.message;
    }
};

/**
 * This will set the new password if the code is valid.
 * The validation of the code is done on the server
 */
const SetNewPassword = async () => {
    try {
        await UserDao.ResetPassword(value.value);
        resetErrormessage.value = '';
        waitingForResetCode.value = false;
        emits('passwordUpdated', true);
    } catch (error: any) {
        resetErrormessage.value = error.message;
        emits('passwordUpdated', false);
    }
};

const passwordMismatch = () => {
    resetErrormessage.value = 'account.passwordmismatch';
};

const passwordMinimumLength = (status: boolean) => {
    resetErrormessage.value = status ? '' : 'account.passwordminimumlength';
};

defineExpose({
    /**
     * Property that tells us if we have submitte a request for a new password and are waiting for the code
     */
    WaitingForResetCode: waitingForResetCode,
    /**
     * Send a request for a reset password token
     */
    RequestNewPassword,
    /**
     * Set the new password
     */
    SetNewPassword,
});
</script>

<template>
    <div>
        <ContentComp v-if="!waitingForResetCode">
            <form class="row g-3 mb-5">
                <div class="col-12">
                    <p v-html="$tm('account.lostpassworddescription', '', true)"></p>
                </div>
                <div class="col-12">
                    <InputText
                        placeholder="account.usernameplaceholder"
                        label="account.username"
                        label-position="top"
                        v-model="value.Username"
                        pattern=".{3,}"
                    />
                </div>
            </form>
        </ContentComp>

        <ContentComp v-else>
            <div class="col-12">
                <p v-html="$tm('account.resetpassworddescription', '', true)"></p>
            </div>
            <div class="text-center mb-5">
                <InputCode
                    v-model="value.Code!"
                    class="form-control-lg"
                    :number-of-inputs="6"
                ></InputCode>
            </div>
            <ResetPassword
                v-model="value"
                @password-mismatch="passwordMismatch"
                @password-minimum-length="passwordMinimumLength"
            ></ResetPassword>
        </ContentComp>

        <div
            v-if="resetErrormessage != ''"
            class="alert alert-danger"
            role="alert"
        >
            <IconComp
                icon="fa-circle-exclamation"
                class="me-2"
            />
            <span class="text-truncate">{{ $tm(resetErrormessage) }}</span>
        </div>
    </div>
</template>
